import {
  Box,
  Col,
  DSIconButton,
  DSIconChevronDown,
  Row,
  css,
  DSListTile,
  DSShortcutKey,
} from "@cdx/ds";
import {Card} from "../../cdx-models/Card";
import {Project} from "../../cdx-models/Project";
import {NoneButton, SharedPropChangeOverlayForChild} from "./shared";
import {sortBy} from "lodash";
import {Account} from "../../cdx-models/Account";
import {Root} from "../../cdx-models/Root";
import {getMySelectedProjectIds} from "../../lib/hooks/useSelectedProjects";
import {TooltipForChild} from "@cdx/common";
import {Sprint} from "../../cdx-models/Sprint";
import {SprintTileContent, getActiveAndFutureSprints} from "../../features/milestones/sprint-utils";
import {SprintTooltipForChild} from "../../pages/milestones/SprintTooltip";
import Helper from "../Helper";
import {Query} from "../../cdx-models/utils/MakeModel";

type MsListOpts = {
  constraints: Query<Sprint>;
  account: Account;
  currentSprint: Sprint | null;
  maxCount: number;
};

const sprintHelper = (
  <Helper
    heading={
      <>
        What are Runs? <DSShortcutKey size="lg">,</DSShortcutKey>
      </>
    }
  >
    <p>
      <i>Runs</i> represent recurring sprints or cycles. They can be used to plan your weekly,
      bi-weekly or monthly progress.
    </p>

    <p>Milestones, Runs and Due Dates can be used in any combination.</p>
  </Helper>
);

const getSprintList = ({constraints, account, currentSprint, maxCount}: MsListOpts) => {
  const nextSprint = getActiveAndFutureSprints({account, constraints, limit: maxCount});
  if (
    !currentSprint ||
    currentSprint.$meta.isDeleted() ||
    nextSprint.some((s) => s.id === currentSprint.id)
  ) {
    return nextSprint;
  } else {
    return sortBy([currentSprint, ...nextSprint].slice(0, maxCount), [
      (s) => s.startDate.year,
      (s) => s.startDate.month,
      (s) => s.startDate.day,
    ]);
  }
};

type SprintPropPickerProps = {
  card: Card;
  onChange: (props: any) => unknown;
  project?: Project;
  root: Root;
};
const SprintPropPicker = (props: SprintPropPickerProps) => {
  const {card, onChange, root, project} = props;
  const projectIds = project
    ? [project.$meta.get("id", null)!].filter(Boolean)
    : getMySelectedProjectIds(root);
  const getConstraints = (): Query<Sprint> => {
    if (card.sprint) {
      return {sprintConfigId: card.sprint.$meta.getId("sprintConfig")};
    } else {
      return projectIds.length > 0 ? {sprintConfig: {sprintProjects: {projectId: projectIds}}} : {};
    }
  };

  const sprintList = getSprintList({
    constraints: getConstraints(),
    account: root.account,
    currentSprint: card.sprint,
    maxCount: 3,
  });

  return (
    <Col sp="12px" pt="16px" pb="24px">
      <Row align="center" sp="8px">
        <Box as="h3" size={12} bold color="primary" textTransform="uppercase">
          Run
        </Box>
        {sprintHelper}
        <SharedPropChangeOverlayForChild
          card={card}
          onChange={onChange}
          cardContainerKey="sidebarSprintPicker"
          availableShortcuts={["sprint"]}
        >
          <TooltipForChild
            tooltip={
              <Box pa="8px">
                Quick select <DSShortcutKey>,</DSShortcutKey>
              </Box>
            }
          >
            <DSIconButton
              variant="secondary"
              size="sm"
              className={css({ml: "auto"})}
              label="Pick"
              icon={<DSIconChevronDown />}
              iconPosition="right"
            />
          </TooltipForChild>
        </SharedPropChangeOverlayForChild>
        <NoneButton onClick={() => onChange({sprintId: null})} active={card.sprint === null} />
      </Row>
      {sprintList.length === 0 ? (
        <Box size={14} color="secondary">
          No upcoming Runs
        </Box>
      ) : (
        <Col sp="4px">
          {sprintList.map((sprint) => (
            <SprintTooltipForChild key={sprint.id} sprint={sprint}>
              <DSListTile
                onClick={() => onChange({sprintId: sprint.id})}
                active={sprint === card.sprint}
              >
                <SprintTileContent sprint={sprint} account={root.account} />
              </DSListTile>
            </SprintTooltipForChild>
          ))}
        </Col>
      )}
    </Col>
  );
};
export default SprintPropPicker;
